import { useState, useEffect } from "react";
import Navbar from "../components/Navbar/Navbar";
import { Link } from "react-router-dom";

// ─── DESIGN TOKENS ────────────────────────────────────────────────
const C = {
  navy:    "#0B1F3A",
  navy2:   "#132d52",
  gold:    "#C9A84C",
  goldL:   "#FDF6E3",
  white:   "#FFFFFF",
  offwhite:"#F8FAFC",
  slate:   "#F1F5F9",
  border:  "#E2E8F0",
  muted:   "#64748B",
  dark:    "#1E293B",
  green:   "#059669",
  greenL:  "#ECFDF5",
  blue:    "#1D4ED8",
  blueL:   "#EFF6FF",
  amber:   "#B45309",
  amberL:  "#FFFBEB",
  red:     "#DC2626",
  redL:    "#FEF2F2",
  purple:  "#7C3AED",
  purpleL: "#F5F3FF",
};


// ── State emoji symbols ───────────────────────────────────────────
const STATE_FLAGS = {
  AL:"🌲", AR:"🌾", AZ:"🌵", CA:"🌉", CO:"🏔️", CT:"⚓", DE:"💎",
  FL:"🌴", GA:"🍑", HI:"🌺", ID:"🥔", IL:"🌽", IN:"🏎️", IA:"🌽",
  KS:"🌾", KY:"🏇", LA:"🎷", ME:"🦞", MD:"🦀", MA:"🦃", MI:"🚗",
  MN:"❄️", MS:"🎵", MO:"🏛️", MT:"🦌", NE:"🌾", NV:"🎰", NH:"🍁",
  NJ:"🗽", NM:"🌶️", NY:"🗽", NC:"⭐", ND:"🌻", OH:"✈️", OK:"🛢️",
  OR:"🌲", PA:"🔔", RI:"⚓", SC:"🌴", SD:"🏔️", TN:"🎵", TX:"⭐",
  UT:"⛷️", VT:"🍁", VA:"🏛️", WA:"🌲", WV:"⛏️", WI:"🧀", WY:"🦬",
};

// ─── STATE DATA ───────────────────────────────────────────────────
const GROUPS = {
  pevo: {
    id: "pevo",
    label: "14 PEVO States",
    sublabel: "Performance-Based",
    color: C.green,
    colorL: C.greenL,
    icon: "🛡️",
    description: "Require demonstrated competency via approved PPCA/PEVO course. States accept reciprocal certificates from recognized national providers.",
    keyRule: "PPCA certificate accepted. Carry original cert + permit at all times.",
    states: [
      { code:"AL", name:"Alabama",       width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO Cert" },
      { code:"AR", name:"Arkansas",      width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO Cert" },
      { code:"CO", name:"Colorado",      width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"w/ lighting",     cert:"PEVO Cert" },
      { code:"CT", name:"Connecticut",   width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",      cert:"PEVO Cert" },
      { code:"DE", name:"Delaware",      width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting",     cert:"PEVO Cert" },
      { code:"FL", name:"Florida",       width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO + Def. Driving" },
      { code:"GA", name:"Georgia",       width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting",     cert:"PEVO Cert" },
      { code:"KY", name:"Kentucky",      width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting",     cert:"PEVO Cert" },
      { code:"LA", name:"Louisiana",     width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO Cert" },
      { code:"MI", name:"Michigan",      width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",      cert:"PEVO Cert" },
      { code:"MS", name:"Mississippi",   width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO Cert" },
      { code:"NC", name:"North Carolina",width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting",     cert:"PEVO Cert" },
      { code:"NM", name:"New Mexico",    width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO Cert" },
      { code:"SC", name:"South Carolina",width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",         cert:"PEVO Cert" },
    ],
  },
  eight: {
    id: "eight",
    label: "8-State Group",
    sublabel: "Uniform Requirements",
    color: C.blue,
    colorL: C.blueL,
    icon: "🔷",
    description: "Operate under identical equipment + signage standards. No state-specific cert required. Single permit package covers all 8 states.",
    keyRule: "Uniform 14'6\" / 14'6\" / 95'0\" across all 8. One equipment standard for all.",
    states: [
      { code:"AZ", name:"Arizona",  width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"ID", name:"Idaho",    width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"No cert" },
      { code:"IA", name:"Iowa",     width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"KS", name:"Kansas",   width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"MO", name:"Missouri", width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"NE", name:"Nebraska", width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"NV", name:"Nevada",   width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"UT", name:"Utah",     width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
    ],
  },
  standalone: {
    id: "standalone",
    label: "Stand-Alone States",
    sublabel: "Independent Requirements",
    color: C.amber,
    colorL: C.amberL,
    icon: "⭐",
    description: "Zero reciprocity. Each state requires its own independent permit or certification. Operating without state-specific credentials is an immediate violation.",
    keyRule: "Obtain state-specific permit BEFORE crossing border. No national cert substitutes.",
    states: [
      { code:"CA", name:"California", width:"14'0\"", height:"14'0\"", length:"95'0\"", ins:"$750K CSL", night:"No Fri 3pm–Mon 6am", cert:"CALTRANS Permit", alert:true },
      { code:"OR", name:"Oregon",     width:"14'6\"", height:"15'6\"", length:"95'0\"", ins:"$750K CSL", night:"Restricted",          cert:"Permit-specific" },
      { code:"TN", name:"Tennessee",  width:"14'6\"", height:"15'0\"", length:"95'0\"", ins:"$750K CSL", night:"w/ lighting",         cert:"No cert" },
      { code:"TX", name:"Texas",      width:"16'0\"", height:"16'0\"", length:"110'0\"",ins:"$750K CSL", night:"Allowed",             cert:"TxDOT License",  alert:true },
      { code:"WI", name:"Wisconsin",  width:"14'6\"", height:"15'0\"", length:"95'0\"", ins:"$750K CSL", night:"w/ lighting",         cert:"No cert" },
      { code:"WY", name:"Wyoming",    width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",             cert:"No cert" },
    ],
  },
  remaining: {
    id: "remaining",
    label: "Remaining 21",
    sublabel: "To Complete All 50",
    color: C.muted,
    colorL: C.slate,
    icon: "🗺️",
    description: "Varied frameworks — some require state permits, others accept PEVO. Key outliers: NY (zero reciprocity, $1M CSL), WA (state permit required), DC ($1M CSL).",
    keyRule: "Always verify individual state requirements. NY and WA are the critical outliers.",
    states: [
      { code:"AK", name:"Alaska",         width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"DC", name:"Wash. DC",       width:"13'0\"", height:"13'6\"", ins:"$1M CSL",   night:"Restricted", cert:"No cert",        alert:true },
      { code:"HI", name:"Hawaii",         width:"10'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert",        alert:true },
      { code:"IL", name:"Illinois",       width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"IN", name:"Indiana",        width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"MD", name:"Maryland",       width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"MA", name:"Massachusetts",  width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"ME", name:"Maine",          width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting",cert:"No cert" },
      { code:"MN", name:"Minnesota",      width:"14'6\"", height:"15'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"MT", name:"Montana",        width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"ND", name:"North Dakota",   width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"OH", name:"Ohio",           width:"14'6\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"OK", name:"Oklahoma",       width:"14'6\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"PA", name:"Pennsylvania",   width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"RI", name:"Rhode Island",   width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"SD", name:"South Dakota",   width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"VA", name:"Virginia",       width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"VT", name:"Vermont",        width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"WV", name:"West Virginia",  width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting",cert:"No cert" },
      { code:"NY", name:"New York",       width:"16'0\"", height:"15'0\"", ins:"$1M CSL",   night:"Restricted", cert:"NYSDOT Required", alert:true },
      { code:"NJ", name:"New Jersey",     width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
      { code:"NH", name:"New Hampshire",  width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
      { code:"WA", name:"Washington",     width:"14'0\"", height:"15'6\"", ins:"$750K CSL", night:"No Fri 3pm–Mon 6am", cert:"WSDOT Permit", alert:true },
    ],
  },
};

const EQUIPMENT = [
  { icon:"🚨", name:"Amber Warning Light",   spec:"360° visibility · 500 ft min · Active during all escort",    ref:"MUTCD 6F" },
  { icon:"🛑", name:"OVERSIZE LOAD Sign",    spec:"18\" × 84\" min · Yellow bg · Black letters · Retroreflective", ref:"MUTCD 6F.56" },
  { icon:"📻", name:"CB Radio",              spec:"Channel 19 default · Must monitor continuously",              ref:"FCC / DOT" },
  { icon:"📱", name:"Two-Way Radio",         spec:"Lead & rear coordination · Required most states",             ref:"State DOT" },
  { icon:"🦺", name:"Hi-Vis ANSI Vest",      spec:"Class 2 in vehicle · Class 3 on roadway · Mandatory",        ref:"OSHA 1910.132" },
  { icon:"🚩", name:"Safety Flags",          spec:"18\" × 18\" min · Red or orange · Load corners required",    ref:"MUTCD 6E.01" },
  { icon:"🧯", name:"Fire Extinguisher",     spec:"2.5 lb ABC min · Mounted & accessible",                      ref:"NFPA 10" },
  { icon:"➕", name:"First Aid Kit",         spec:"OSHA Class A min · Fully stocked · Within expiration",       ref:"OSHA 1910.151" },
  { icon:"✋", name:"STOP / SLOW Paddle",    spec:"18\" diameter min · MUTCD compliant · Flagging ops",         ref:"MUTCD 6E.03" },
  { icon:"🔺", name:"Reflective Triangles",  spec:"3 minimum · 10 / 100 / 300 ft behind stopped vehicle",      ref:"DOT 393.95" },
  { icon:"🔋", name:"Spare Batteries",       spec:"Lights, radios, equipment · Keep charged & accessible",      ref:"Best Practice" },
  { icon:"🔦", name:"Flashlight",            spec:"LED recommended · 200 lm min · Extra batteries on board",    ref:"Best Practice" },
  { icon:"📄", name:"Permit Documents",      spec:"Original permit + route plan + insurance cert in vehicle",   ref:"State DOT" },
  { icon:"🗺️", name:"State-Specific Req.",  spec:"State escort permit where required · Follow travel restrictions",ref:"State DOT" },
];

const UNIVERSALS = [
  { label:"OVERSIZE LOAD Sign",  value:'18" × 84"',     sub:"Yellow bg · Black letters" },
  { label:"Amber Light Range",   value:"500 ft",         sub:"360° visibility minimum" },
  { label:"Safety Flags",        value:'18" × 18"',     sub:"Red or orange — all states" },
  { label:"CB Radio Channel",    value:"Ch. 19",         sub:"Default — all states" },
  { label:"Fire Extinguisher",   value:"2.5 lb ABC",    sub:"Mounted & accessible" },
  { label:"STOP/SLOW Paddle",    value:'18" min',       sub:"OCTAGONAL = STOP · RECT = SLOW" },
  { label:"Lead Escort Gap",     value:"500 ft",         sub:"Ahead on open highway" },
  { label:"Rear Escort Gap",     value:"100–300 ft",    sub:"Behind trailing load" },
  { label:"Insurance Baseline",  value:"$750K CSL",      sub:"National floor — some states higher" },
  { label:"ANSI Vest Roadway",   value:"Class 3",        sub:"Class 2 minimum in vehicle" },
];

// State abbreviation → full DOT logo text
const STATE_DOT = {
  CA:"Caltrans", TX:"TxDOT", WA:"WSDOT", NY:"NYSDOT", FL:"FDOT",
  OH:"ODOT", GA:"GDOT", CO:"CDOT", AL:"ALDOT", AR:"ARDOT",
  AZ:"ADOT", ID:"ITD", IA:"IaDOT", KS:"KDOT", MO:"MoDOT",
  NE:"NDOT", NV:"NDOT", UT:"UDOT", CT:"ConnDOT", DE:"DelDOT",
  KY:"KYTC", LA:"LADOTD", MI:"MDOT", MS:"MDOT", NC:"NCDOT",
  NM:"NMDOT", SC:"SCDOT", OR:"ODOT", TN:"TDOT", WI:"WisDOT",
  WY:"WYDOT", AK:"ADOT", DC:"DDOT", HI:"HDOT", IL:"IDOT",
  IN:"INDOT", MD:"SHA", MA:"MassDOT", ME:"MaineDOT", MN:"MnDOT",
  MT:"MDT", ND:"NDDOT", OK:"ODOT", PA:"PennDOT", RI:"RIDOT",
  SD:"SDDOT", VA:"VDOT", VT:"VTrans", WV:"WVDOH", NJ:"NJDOT", NH:"NHDOT",
};

// ─── MICRO COMPONENTS ─────────────────────────────────────────────

function DotLogo({ code, size = 32 }) {
  const dot = STATE_DOT[code] || `${code}DOT`;
  return (
    <div style={{
      width: size, height: size, borderRadius: 4,
      background: C.navy, border: `1px solid ${C.gold}55`,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>
      <span style={{ fontSize: size * 0.22, fontWeight: 900, color: C.gold, lineHeight: 1, letterSpacing: "-0.02em" }}>
        {dot}
      </span>
    </div>
  );
}

function Pill({ text, color, light }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
      padding: "2px 7px", borderRadius: 3,
      background: light, color,
      border: `1px solid ${color}33`,
      whiteSpace: "nowrap",
    }}>{text}</span>
  );
}

function SectionEyebrow({ text }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, marginBottom: 18,
    }}>
      <div style={{ flex: 1, height: 1, background: C.border }} />
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: C.muted }}>
        {text}
      </span>
      <div style={{ flex: 1, height: 1, background: C.border }} />
    </div>
  );
}

function StatCard({ s, groupColor, isDark }) {
  const bg     = isDark ? "#142a4d"               : C.white;
  const bdr    = isDark ? "rgba(255,255,255,0.10)" : C.border;
  const headC  = isDark ? "#f0f4f8"               : C.navy;
  const subC   = isDark ? "rgba(240,244,248,0.55)": C.muted;
  const bodyC  = isDark ? "rgba(240,244,248,0.80)": C.dark;
  const flag   = STATE_FLAGS[s.code] || "🏳️";

  return (
    <div style={{
      background: bg, border: `1px solid ${bdr}`,
      borderRadius: 14, padding: "16px",
      borderTop: `3px solid ${groupColor}`,
      display: "flex", flexDirection: "column", gap: 10,
      transition: "box-shadow 0.15s, transform 0.15s",
      cursor: "default",
    }}
    onMouseEnter={e=>{e.currentTarget.style.boxShadow=`0 6px 24px ${groupColor}25`;e.currentTarget.style.transform="translateY(-2px)";}}
    onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.transform="none";}}>

      {/* Header: flag + code + name */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{flag}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: headC, letterSpacing: "-0.02em", lineHeight: 1 }}>
              {s.code}
            </div>
            <div style={{ fontSize: 12, color: subC, marginTop: 2, fontWeight: 500 }}>{s.name}</div>
          </div>
        </div>
        {s.alert && <span style={{ fontSize: 16 }}>⚠️</span>}
      </div>

      {/* Stats grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr",
        gap: "6px 12px", padding: "10px 0",
        borderTop: `1px solid ${bdr}`, borderBottom: `1px solid ${bdr}`,
      }}>
        {[["W", s.width], ["H", s.height], ["Ins.", s.ins], ["Night", s.night]].map(([l, v]) => (
          <div key={l}>
            <span style={{ fontSize: 10, color: subC, fontWeight: 700, display: "block" }}>{l}</span>
            <span style={{ fontSize: 13, color: bodyC, fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Cert badge + view guide button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{
          fontSize: 12, fontWeight: 700,
          color: s.alert ? C.red : groupColor,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <span>{s.alert ? "⚠️" : "✓"}</span>{s.cert}
        </div>
        <Link to="/training" style={{
          fontSize: 11, fontWeight: 800, color: isDark ? "#0d1f3c" : "#fff",
          textDecoration: "none", padding: "5px 12px", borderRadius: 99,
          background: groupColor,
          border: `1px solid ${groupColor}`,
          whiteSpace: "nowrap", transition: "all .15s",
          boxShadow: `0 2px 8px ${groupColor}40`,
        }}
        onMouseEnter={e=>{e.currentTarget.style.filter="brightness(1.10)";}}
        onMouseLeave={e=>{e.currentTarget.style.filter="none";}}>
          Study Guide →
        </Link>
      </div>
    </div>
  );
}

// ─── SECTION: FRAMEWORK INFOGRAPHIC ──────────────────────────────
function FrameworkInfographic({ isDark }) {
  const [expanded, setExpanded] = useState(null);
  const groups = Object.values(GROUPS);
  const bg      = isDark ? "#142a4d" : C.white;
  const midBg   = isDark ? "#1a3358" : C.slate;
  const bdr     = isDark ? "rgba(255,255,255,0.10)" : C.border;
  const textC   = isDark ? "#f0f4f8" : C.dark;
  const textSub = isDark ? "rgba(240,244,248,0.65)" : C.muted;

  const details = [
    { title:"PEVO ↔ 14 States", color:C.green, icon:"🛡️",
      body:"Your PPCA certificate is the passport. Present it at each PEVO state with the original permit and you are compliant across all 14 states without additional applications.",
      rule:"Carry original cert + permit at all times.", states:"AL, AR, CO, CT, DE, FL, GA, KY, LA, MI, MS, NC, NM, SC" },
    { title:"8-State → Single Kit", color:C.blue, icon:"🔷",
      body:"One correctly-equipped vehicle satisfies all 8 states simultaneously. Uniform 14'6\" W / 14'6\" H / 95'0\" L thresholds — memorize once, apply everywhere.",
      rule:"Uniform 14'6\" / 14'6\" / 95'0\" across all 8.", states:"AZ, ID, IA, KS, MO, NE, NV, UT" },
    { title:"Stand-Alone → Separate Permit", color:C.amber, icon:"⭐",
      body:"Each stand-alone state requires its own credential obtained before crossing the border. CA, TX, and WA are the highest-priority states to pre-qualify for.",
      rule:"Get state-specific permit BEFORE crossing.", states:"CA, NY, TX, WA, OR, NJ" },
    { title:"Remaining 21 — Verify Each", color:C.muted, icon:"🗺️",
      body:"These states have varied frameworks — some require state permits, others accept PEVO. Always verify current requirements with the state DOT permit authority.",
      rule:"Check state DOT before every permitted move.", states:"HI, AK, MT, WY, ND, SD, MN, WI, IN, OH, WV, VA, MD, PA, NY, NH, VT, ME, RI, MA" },
  ];

  return (
    <div style={{ padding:"20px 0 24px" }}>
      <SectionEyebrow text="HOW THE 4-GROUP FRAMEWORK WORKS TOGETHER" />

      {/* ── STAT SUMMARY ROW ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginBottom:20 }}>
        {groups.map(g => (
          <div key={g.id} style={{ borderRadius:12, overflow:"hidden", border:`1px solid ${g.color}44` }}>
            <div style={{ background:g.color, padding:"10px 14px", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:18 }}>{g.icon}</span>
              <div style={{ fontSize:12, fontWeight:800, color:C.white, lineHeight:1.2 }}>{g.label}</div>
            </div>
            <div style={{ background:isDark?`${g.color}18`:g.colorL, padding:"10px 14px" }}>
              <div style={{ fontSize:24, fontWeight:900, color:g.color, lineHeight:1 }}>{g.states.length}</div>
              <div style={{ fontSize:10, color:textSub, marginTop:3, lineHeight:1.4 }}>{g.sublabel}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── ACCORDION CARDS — tap to expand ── */}
      <div style={{ fontSize:11, fontWeight:700, color:textSub, letterSpacing:".08em", textTransform:"uppercase", marginBottom:10 }}>
        Tap any framework to learn more
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
        {details.map((d, i) => {
          const open = expanded === i;
          return (
            <div key={i} style={{ borderRadius:14, border:`2px solid ${open ? d.color : bdr}`, overflow:"hidden", transition:"border-color .2s", background:bg }}>
              {/* Header — always visible, tap to expand */}
              <button onClick={() => setExpanded(open ? null : i)}
                style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit", textAlign:"left", gap:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:10, background:`${d.color}20`, border:`1.5px solid ${d.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                    {d.icon}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:open ? d.color : textC, lineHeight:1.2 }}>{d.title}</div>
                    <div style={{ fontSize:11, color:textSub, marginTop:2 }}>
                      {d.states.split(",").length} states · {open ? "tap to collapse" : "tap to expand"}
                    </div>
                  </div>
                </div>
                <div style={{ width:28, height:28, borderRadius:"50%", background:`${d.color}15`, border:`1px solid ${d.color}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"transform .2s", transform:open?"rotate(180deg)":"none", color:d.color, fontSize:16, fontWeight:700 }}>
                  ›
                </div>
              </button>

              {/* Expanded content */}
              {open && (
                <div style={{ padding:"0 16px 16px", borderTop:`1px solid ${bdr}` }}>
                  <p style={{ fontSize:13, color:textSub, lineHeight:1.75, margin:"14px 0 12px" }}>{d.body}</p>
                  <div style={{ padding:"10px 14px", borderRadius:10, background:`${d.color}12`, border:`1px solid ${d.color}25`, marginBottom:12 }}>
                    <div style={{ fontSize:10, fontWeight:800, color:d.color, textTransform:"uppercase", letterSpacing:".06em", marginBottom:4 }}>Key Rule</div>
                    <div style={{ fontSize:13, fontWeight:700, color:textC }}>{d.rule}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:textSub, textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>States in this group</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                      {d.states.split(", ").map(st => (
                        <span key={st} style={{ padding:"3px 8px", borderRadius:6, background:`${d.color}15`, border:`1px solid ${d.color}30`, fontSize:11, fontWeight:700, color:d.color }}>{st}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── DECISION FLOW — simple linear steps ── */}
      <div style={{ background:midBg, borderRadius:14, padding:"16px", border:`1px solid ${bdr}` }}>
        <div style={{ fontSize:11, fontWeight:800, color:textSub, textTransform:"uppercase", letterSpacing:".08em", marginBottom:14, textAlign:"center" }}>Quick Decision Flow</div>
        <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
          {[
            { step:"1", label:"Am I PPCA certified?", yes:"→ Work in all 14 PEVO states", color:C.green },
            { step:"2", label:"Moving through AZ, ID, IA, KS, MO, NE, NV, or UT?", yes:"→ One equipment kit covers all 8", color:C.blue },
            { step:"3", label:"Entering CA, TX, NY, WA, or OR?", yes:"→ Get state permit first", color:C.amber },
            { step:"4", label:"Other state?", yes:"→ Check that state's DOT requirements", color:C.muted },
          ].map((s, i) => (
            <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", paddingBottom: i < 3 ? 14 : 0, marginBottom: i < 3 ? 14 : 0, borderBottom: i < 3 ? `1px solid ${bdr}` : "none" }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:s.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color:"#fff", flexShrink:0, marginTop:1 }}>{s.step}</div>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:textC, marginBottom:3 }}>{s.label}</div>
                <div style={{ fontSize:12, fontWeight:700, color:s.color }}>{s.yes}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SECTION: UNIVERSAL STANDARDS ────────────────────────────────
function UniversalStandards() {
  return (
    <div style={{ padding: "24px 0" }}>
      <SectionEyebrow text="WHAT IS IDENTICAL ACROSS ALL 50 STATES" />
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8,
      }}>
        {UNIVERSALS.map((u, i) => (
          <div key={i} style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: "12px 10px", textAlign: "center",
            borderTop: `3px solid ${C.gold}`,
          }}>
            <div style={{
              fontSize: 18, fontWeight: 900, color: C.navy,
              lineHeight: 1.1, marginBottom: 4, letterSpacing: "-0.02em",
            }}>{u.value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.dark, marginBottom: 3 }}>
              {u.label}
            </div>
            <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.4 }}>{u.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SECTION: EQUIPMENT CHECKLIST ────────────────────────────────
function EquipmentChecklist() {
  const [checked, setChecked] = useState({});
  const count = Object.values(checked).filter(Boolean).length;

  return (
    <div style={{ padding: "24px 0" }}>
      <SectionEyebrow text="14-ITEM PRE-TRIP CHECKLIST · ALL STATES" />
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 14,
      }}>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
          {count}/{EQUIPMENT.length} verified
        </span>
        <div style={{
          height: 4, width: 220, background: C.border, borderRadius: 99, overflow: "hidden",
        }}>
          <div style={{
            height: "100%", borderRadius: 99,
            width: `${(count / EQUIPMENT.length) * 100}%`,
            background: `linear-gradient(90deg, ${C.gold}, #F5D680)`,
            transition: "width 0.4s ease",
          }} />
        </div>
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6,
      }}>
        {EQUIPMENT.map((e, i) => (
          <div
            key={i}
            onClick={() => setChecked(p => ({ ...p, [i]: !p[i] }))}
            style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "10px 12px", borderRadius: 7, cursor: "pointer",
              background: checked[i] ? C.greenL : C.white,
              border: `1px solid ${checked[i] ? C.green + "66" : C.border}`,
              transition: "all 0.15s",
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: 3, flexShrink: 0, marginTop: 1,
              background: checked[i] ? C.green : "transparent",
              border: `1.5px solid ${checked[i] ? C.green : C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, color: C.white,
            }}>
              {checked[i] && "✓"}
            </div>
            <span style={{ fontSize: 15, flexShrink: 0 }}>{e.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: C.navy,
                textDecoration: checked[i] ? "line-through" : "none",
                opacity: checked[i] ? 0.6 : 1,
              }}>{e.name}</div>
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, marginTop: 1 }}>
                {e.spec}
              </div>
            </div>
            <span style={{
              fontSize: 9, color: C.muted, flexShrink: 0,
              background: C.slate, padding: "2px 5px", borderRadius: 3,
              alignSelf: "flex-start", whiteSpace: "nowrap",
            }}>{e.ref}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SECTION: GROUP DETAIL ────────────────────────────────────────
function GroupSection({ group, isDark }) {
  const [expanded, setExpanded] = useState(true);
  const g = GROUPS[group];

  return (
    <div style={{ padding: "24px 0", borderTop: `1px solid ${C.border}` }}>
      {/* Group header */}
      <div
        onClick={() => setExpanded(p => !p)}
        style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", cursor: "pointer", marginBottom: expanded ? 20 : 0,
        }}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{
            width: 44, height: 44, borderRadius: 8,
            background: g.color, display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: 22, flexShrink: 0,
          }}>
            {g.icon}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 800, color: C.navy, letterSpacing: "-0.02em" }}>
                {g.label}
              </span>
              <Pill text={g.sublabel} color={g.color} light={g.colorL} />
              <Pill text={`${g.states.length} states`} color={C.muted} light={C.slate} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, maxWidth: 580 }}>
              {g.description}
            </div>
          </div>
        </div>
        <span style={{
          fontSize: 18, color: C.muted,
          transform: expanded ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }}>▾</span>
      </div>

      {expanded && (
        <>
          {/* Key rule banner */}
          <div style={{
            padding: "10px 14px", borderRadius: 7,
            background: `${g.color}11`, border: `1px solid ${g.color}33`,
            fontSize: 11, color: C.dark, lineHeight: 1.6,
            marginBottom: 16,
            display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <span style={{ color: g.color, fontWeight: 800, flexShrink: 0 }}>KEY RULE:</span>
            {g.keyRule}
          </div>

          {/* State cards grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
            gap: 10,
          }}>
            {g.states.map(s => (
              <StatCard key={s.code} s={s} groupColor={g.color} isDark={isDark} />
            ))}
          </div>

          {/* Alerts if any */}
          {g.states.some(s => s.alert) && (
            <div style={{
              marginTop: 14, padding: "12px 16px", borderRadius: 7,
              background: C.redL, border: `1px solid ${C.red}33`,
              display: "flex", gap: 10, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
              <div style={{ fontSize: 11, color: C.dark, lineHeight: 1.6 }}>
                <strong style={{ color: C.red }}>HIGH ALERT states in this group:</strong>{" "}
                {g.states.filter(s => s.alert).map(s => s.code).join(", ")} —
                These states impose requirements that go beyond the group standard.
                Verify individually before crossing their borders.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── SECTION: RED FLAGS ───────────────────────────────────────────
function RedFlags() {
  const flags = [
    { state:"CA", title:"California Weekend Ban", body:"NO oversize moves Fri 3 PM → Mon 6 AM on most routes. Holiday blackouts add Memorial Day, Labor Day, Thanksgiving, Christmas, New Year's. Width is irrelevant — all permitted loads are restricted." },
    { state:"NY", title:"New York — Zero Reciprocity", body:"NYSDOT-specific credential mandatory. No national cert substitutes. Police escort required for loads >13'6\" wide. Insurance minimum $1M CSL. Fines start at $5,000 per violation." },
    { state:"TX", title:"Texas Escort License", body:"TxDOT Escort License required (2-year validity). Loads >14'0\" need front escort. Over 16'0\" → front AND rear. Over 20'0\" wide or 200,000 lbs → Texas DPS police escort." },
    { state:"WA", title:"Washington WSDOT Permit + Ferry Rules", body:"WSDOT Pilot Car Permit required separately from PEVO. Permits expire midnight — no grace. Ferry crossings must be explicitly authorized. WSP escort required >16'0\" wide or >200,000 lbs." },
    { state:"ALL", title:"Missing Permit in Vehicle", body:"Original permit (not photocopy) must be present in escort vehicle at ALL times. CA, TX, NY, WA, and FL issue immediate stop orders for missing documents. No exceptions." },
    { state:"ALL", title:"Superload Route Survey Omission", body:"Any load >16'0\" wide, >16'0\" tall, or >150,000 lbs requires a pre-move route survey in all 50 states. Omitting this voids your liability coverage in most commercial policies." },
    { state:"HI", title:"Hawaii — 10'0\" Width Limit", body:"Hawaii's island road infrastructure limits most moves to just 10'0\" wide — dramatically below the mainland baseline of 14'0\"–14'6\". Operators frequently overlook this constraint." },
    { state:"DC", title:"Washington DC — Federal Overlay", body:"DDOT + National Park Service + Capitol Police coordination may all be required depending on route. Insurance minimum is $1M CSL. Extremely restricted move windows." },
  ];

  return (
    <div style={{ padding: "24px 0", borderTop: `1px solid ${C.border}` }}>
      <SectionEyebrow text="RED FLAGS — CATASTROPHIC COMPLIANCE TRAPS" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {flags.map((f, i) => (
          <div key={i} style={{
            padding: "12px 14px", borderRadius: 8,
            background: C.redL, border: `1px solid ${C.red}22`,
            display: "flex", gap: 10,
          }}>
            <div style={{
              flexShrink: 0, width: 34, height: 34, borderRadius: 5,
              background: C.red, color: C.white,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 800, letterSpacing: "0.02em",
              textAlign: "center", lineHeight: 1.2,
            }}>
              {f.state === "ALL" ? "ALL" : <DotLogo code={f.state} size={34} />}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.red, marginBottom: 4 }}>
                {f.title}
              </div>
              <div style={{ fontSize: 10, color: C.dark, lineHeight: 1.6 }}>{f.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SECTION: QUICK REF TABLE ─────────────────────────────────────
function QuickRefTable() {
  const [filter, setFilter] = useState("all");
  const allStates = Object.values(GROUPS).flatMap(g =>
    g.states.map(s => ({ ...s, group: g.id, groupColor: g.color, groupLabel: g.label }))
  );
  const filtered = filter === "all" ? allStates : allStates.filter(s => s.group === filter);

  return (
    <div style={{ padding: "24px 0", borderTop: `1px solid ${C.border}` }}>
      <SectionEyebrow text="ALL 50 STATES QUICK REFERENCE" />

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[["all", "All 50 States", C.navy], ["pevo", "14 PEVO", C.green],
          ["eight", "8-State", C.blue], ["standalone", "Stand-Alone", C.amber],
          ["remaining", "Remaining 21", C.muted]
        ].map(([key, label, color]) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            padding: "5px 12px", borderRadius: 5, border: "none", cursor: "pointer",
            background: filter === key ? color : C.slate,
            color: filter === key ? C.white : C.muted,
            fontWeight: filter === key ? 700 : 500, fontSize: 11,
            transition: "all 0.15s",
          }}>{label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: C.navy }}>
              {["State","Group","Max Width","Max Height","Insurance","Night Travel","Cert Required"].map(h => (
                <th key={h} style={{
                  padding: "8px 10px", textAlign: "left",
                  color: C.gold, fontWeight: 700, fontSize: 10,
                  letterSpacing: "0.05em", whiteSpace: "nowrap",
                  borderBottom: `2px solid ${C.gold}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, i) => (
              <tr key={s.code} style={{
                background: i % 2 === 0 ? C.white : C.offwhite,
                borderBottom: `1px solid ${C.border}`,
              }}>
                <td style={{ padding: "7px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <DotLogo code={s.code} size={22} />
                    <div>
                      <div style={{ fontWeight: 700, color: C.navy }}>{s.code}</div>
                      <div style={{ fontSize: 9, color: C.muted }}>{s.name}</div>
                    </div>
                    {s.alert && <span style={{ fontSize: 11 }}>⚠️</span>}
                  </div>
                </td>
                <td style={{ padding: "7px 10px" }}>
                  <Pill text={s.groupLabel} color={s.groupColor} light={`${s.groupColor}11`} />
                </td>
                <td style={{ padding: "7px 10px", fontWeight: 600, color: C.dark }}>{s.width}</td>
                <td style={{ padding: "7px 10px", fontWeight: 600, color: C.dark }}>{s.height}</td>
                <td style={{ padding: "7px 10px", color: s.ins !== "$750K CSL" ? C.red : C.dark, fontWeight: s.ins !== "$750K CSL" ? 700 : 400 }}>{s.ins}</td>
                <td style={{ padding: "7px 10px", fontSize: 10, color: C.dark }}>{s.night}</td>
                <td style={{ padding: "7px 10px" }}>
                  <span style={{
                    fontSize: 10, fontWeight: s.cert !== "No cert" ? 700 : 400,
                    color: s.cert !== "No cert" ? C.red : C.green,
                  }}>{s.cert}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────
export default function PPCACrashCourse({ isDarkMode, toggleTheme }) {
  const tabs = [
    { id: "framework",  label: "Framework",  icon: "🗺️"  },
    { id: "universals", label: "Universals", icon: "📐"  },
    { id: "equipment",  label: "Equipment",  icon: "🔧"  },
    { id: "pevo",       label: "14 PEVO",    icon: "🛡️"  },
    { id: "eight",      label: "8-State",    icon: "🔷"  },
    { id: "standalone", label: "Stand-Alone",icon: "⭐"  },
    { id: "remaining",  label: "Remaining",  icon: "🗺️"  },
    { id: "redflags",   label: "Red Flags",  icon: "🚨"  },
    { id: "allstates",  label: "All States", icon: "📊"  },
  ];
  const [activeTab, setActiveTab] = useState("framework");
  // sync with parent theme toggle; fallback to localStorage
  const [localDark, setLocalDark] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("theme") === "dark"
  );
  const D = isDarkMode !== undefined ? isDarkMode : localDark;
  const toggleDark = () => {
    const next = !localDark;
    setLocalDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
    if (toggleTheme) toggleTheme();
  };

  return (
    <div style={{
      fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
      background: D ? "#0d1f3c" : C.offwhite, minHeight: "100vh", color: D ? "#f0f4f8" : C.dark, transition: "background .3s",
    }}>

      {/* ── SITE NAVBAR ── */}
      <Navbar isDarkMode={D} toggleTheme={toggleDark} />

      {/* ── PPCA SUB-TAB BAR ── */}
      <div style={{
        background: D ? "#0d1f3c" : C.navy,
        borderBottom: `2px solid ${C.gold}`,
        position: "sticky", top: 62, zIndex: 200,
      }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 16px" }}>
          {/* PPCA course identity row */}
          
          {/* Sub-tabs with gradient scroll hint */}
          <div style={{ position: "relative" }}>
            <div id="study-tabs" style={{ display: "flex", gap: 0, overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                  padding: "11px 14px", border: "none", cursor: "pointer",
                  background: activeTab === t.id ? "rgba(201,168,76,0.15)" : "transparent",
                  color: activeTab === t.id ? C.gold : `${C.white}70`,
                  fontWeight: activeTab === t.id ? 800 : 500,
                  fontSize: 13, display: "flex", alignItems: "center", gap: 5,
                  borderBottom: `3px solid ${activeTab === t.id ? C.gold : "transparent"}`,
                  borderRadius: activeTab === t.id ? "6px 6px 0 0" : 0,
                  transition: "all .15s", whiteSpace: "nowrap",
                  fontFamily: "inherit", flexShrink: 0,
                }}>
                  <span style={{ fontSize: 13 }}>{t.icon}</span>{t.label}
                </button>
              ))}
            </div>
            {/* Gradient fade on right = more tabs hint */}
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 40, background: "linear-gradient(to right, transparent, #0d1f3c)", pointerEvents: "none" }}/>
            <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: C.gold, fontSize: 16, pointerEvents: "none", opacity: 0.8 }}>›</div>
          </div>
        </div>
      </div>

      {/* ── HERO STRIP ── */}
      <div style={{
        background: `linear-gradient(135deg, #0d1f3c 0%, #142a4d 100%)`,
        borderBottom: `1px solid ${C.gold}33`,
        padding: "28px 24px",
      }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr auto", gap: "clamp(16px, 4vw, 40px)",
            alignItems: "center", flexWrap: "wrap",
          }}>
            <div>
              <div style={{
                fontSize: 10, fontWeight: 700, color: C.gold,
                letterSpacing: "0.12em", marginBottom: 10,
              }}>
                PPCA PRE-EXAM CRASH COURSE
              </div>
              <h1 style={{
                fontSize: 28, fontWeight: 900, color: C.white,
                margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15,
              }}>
                All 50 States{" "}
                <span style={{ color: C.gold }}>PEVO</span>{" "}
                Compliance Guide
              </h1>
              <p style={{
                fontSize: 13, color: `${C.white}99`, margin: 0, lineHeight: 1.6,
              }}>
                4-group framework · Universal equipment standards · State-specific red flags
              </p>
            </div>
            {/* Stat pills */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { n:"50", l:"States Covered" },
                { n:"4",  l:"Compliance Groups" },
                { n:"14", l:"Equipment Items" },
                { n:"70%",l:"Passing Score" },
              ].map((s, i) => (
                <div key={i} style={{
                  padding: "10px 14px", borderRadius: 7,
                  background: `${C.white}08`,
                  border: `1px solid ${C.gold}22`,
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: C.gold, lineHeight: 1 }}>
                    {s.n}
                  </div>
                  <div style={{ fontSize: 9, color: `${C.white}77`, marginTop: 3 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 clamp(12px, 4vw, 24px) 48px" }}>

        {activeTab === "framework"  && <FrameworkInfographic isDark={D} />}
        {activeTab === "universals" && <UniversalStandards />}
        {activeTab === "equipment"  && <EquipmentChecklist />}
        {activeTab === "pevo"       && <GroupSection group="pevo" isDark={D} />}
        {activeTab === "eight"      && <GroupSection group="eight" isDark={D} />}
        {activeTab === "standalone" && <GroupSection group="standalone" isDark={D} />}
        {activeTab === "remaining"  && <GroupSection group="remaining" isDark={D} />}
        {activeTab === "redflags"   && <RedFlags />}
        {activeTab === "allstates"  && <QuickRefTable />}

        {/* Footer disclaimer */}
        <div style={{
          marginTop: 32, padding: "12px 16px", borderRadius: 7,
          background: C.redL, border: `1px solid ${C.red}22`,
          fontSize: 10, color: C.red, lineHeight: 1.6,
        }}>
          ⚠️ <strong>IMPORTANT:</strong> This PPCA training content is for educational pre-exam preparation only.
          It does not constitute state certification or authorization to operate as a pilot/escort vehicle operator.
          Always verify current requirements with your state DOT permit authority before every permitted move.
          Regulations change — confirm before each operation. © {new Date().getFullYear()} PilotLinkUSA
        </div>
      </div>
    </div>
  );
}
